import { useEffect, useRef } from "react";

import { ApiError } from "@/api/client";
import { useEnedisProbe } from "@/hooks/useEnedisProbe";
import type { Site } from "@/types";

/**
 * Orchestration des probes Enedis « PRM validé » dans « Mes sites » (S14-7, A2).
 *
 * Deux déclenchements, **non bloquants** et **bornés** :
 * - **Warm-up** au montage de la page (= après login, « Mes sites » étant la
 *   première vue où l'on gère Enedis) : énumère les sites
 *   `has_prm && !prm_validated` et émet un probe par site, **≤ 2-3 concurrents**
 *   (ne pas saturer le token CC partagé, § 4.bis « Cadence / quota »).
 * - **Lazy** : tout site `has_prm && !prm_validated` qui apparaît ensuite (ajout,
 *   nouvelle pose de PRM) est probé une fois également.
 *
 * **1× par session, sur verdict DÉFINITIF seulement** : un `Set` de session
 * (module-level, survit aux remontages de composant ; réinitialisé au reload
 * complet **et à chaque transition d'identité** via `resetEnedisProbeSession()`
 * — le statut survit via `prm_validated_at` persisté en BDD)
 * garantit qu'un site au verdict définitif n'est jamais re-probé. Un site est
 * marqué « traité » **uniquement** quand le probe rend un verdict serveur
 * (réponse 200, succès ou échec consentement/identité délibéré). Au succès,
 * `useEnedisProbe` remplace l'entrée de cache (statut figé session).
 *
 * **Verdict INDÉTERMINÉ** (token CC momentanément down → 401, 5xx, erreur
 * réseau : la mutation **rejette**) : le site n'est PAS marqué → il redevient
 * éligible au prochain déclenchement du warm-up (remontage / changement de
 * `sites`), après rétablissement d'Enedis. Une garde transitoire `inProgress`
 * empêche un re-enfilage tant que le probe est en vol (pas de retry en boucle
 * immédiat dans la même passe).
 *
 * Erreurs avalées (probe non bloquant) : un 400/403/404/réseau ne casse pas la
 * page ; le statut reste « non validé » et l'UI affiche un re-test manuel.
 */

/** Concurrence max (≤ 2-3, § 4.bis). */
const MAX_CONCURRENT_PROBES = 2;

/**
 * Sites déjà probés dans la session courante (1× par session). Module-level
 * volontairement : partagé entre tous les montages de la page pendant la
 * session de l'onglet. Réinitialisé au reload (le statut persiste en BDD).
 *
 * **État owner-scoped** : il ne vaut que pour l'identité courante. À chaque
 * transition d'identité (login/logout/signup/suppression de compte), il DOIT
 * être purgé — au même titre que le cache react-query — via
 * `resetEnedisProbeSession()`, sans quoi les ids probés par le compte A
 * subsisteraient et exclueraient à tort du warm-up les sites homonymes du
 * compte B dans le même onglet (fuite d'état cross-compte).
 */
const probedThisSession = new Set<string>();

/**
 * Purge le suivi de session du probe Enedis (état owner-scoped module-level).
 * Appelé à chaque transition d'identité depuis `useAuth` (à côté du purge de
 * cache react-query), et réutilisé par les tests.
 */
export function resetEnedisProbeSession(): void {
  probedThisSession.clear();
}

export interface UseEnedisProbeWarmup {
  /** `true` si un probe est en cours (au moins une requête en vol). */
  isProbing: boolean;
}

export function useEnedisProbeWarmup(
  sites: Site[] | undefined,
): UseEnedisProbeWarmup {
  const probe = useEnedisProbe();
  // File d'attente locale + nombre de requêtes en vol (concurrence bornée).
  const inFlight = useRef<number>(0);
  const queue = useRef<string[]>([]);
  // Garde transitoire : sites en file ou en vol *dans cette passe*. Distinct du
  // Set de session (`probedThisSession`, verdict définitif) : `inProgress`
  // empêche le re-enfilage tant que le probe tourne, mais est libéré à la
  // complétion — un verdict INDÉTERMINÉ redevient donc éligible au prochain
  // déclenchement du warm-up.
  const inProgress = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!sites) return;

    // Cible : PRM présent mais non validé, sans verdict définitif cette session,
    // pas déjà en file/en vol (garde transitoire).
    const candidates = sites
      .filter((s) => s.has_prm && !s.prm_validated)
      .map((s) => s.id)
      .filter((id) => !probedThisSession.has(id) && !inProgress.current.has(id));

    if (candidates.length === 0) return;

    for (const id of candidates) {
      inProgress.current.add(id);
      queue.current.push(id);
    }

    const pump = (): void => {
      while (inFlight.current < MAX_CONCURRENT_PROBES && queue.current.length > 0) {
        const id = queue.current.shift();
        if (id === undefined) break;
        inFlight.current += 1;
        probe
          .mutateAsync(id)
          .then(() => {
            // Verdict DÉFINITIF (réponse 200, succès ou échec consentement/PRM
            // délibéré) : on fige le site pour la session — plus de re-probe.
            probedThisSession.add(id);
          })
          .catch((err: unknown) => {
            // Verdict INDÉTERMINÉ (401 token CC / 5xx / réseau, ou 400/403/404) :
            // probe non bloquant, on avale. Le site n'est PAS marqué → il
            // redeviendra éligible au prochain déclenchement du warm-up. Le
            // re-test manuel reste disponible côté carte.
            void (err instanceof ApiError ? err : err);
          })
          .finally(() => {
            // Libère la garde transitoire : ré-éligible au prochain warm-up si
            // non figé (verdict indéterminé).
            inProgress.current.delete(id);
            inFlight.current -= 1;
            pump();
          });
      }
    };
    pump();
    // `probe` est stable (mutation react-query) ; on ne dépend que de `sites`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  return { isProbing: probe.isPending };
}
